import { pick } from 'lodash';
import React from 'react';
import Modal from 'react-modal';
import { connect, Dispatch } from 'react-redux';
import { InjectedRouter } from 'react-router';
import { bindActionCreators } from 'redux';

import { withRouter } from 'app/context/Router';
import CurrentUser from 'auth/CurrentUser';
import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import ConfirmDialog from 'components/ConfirmDialog';
import Dialog from 'components/Dialog';
import { Message, Target } from 'entities/Message';
import type { ChatPredefinedMessageTranslationsInput } from 'globalTypes.hq';
import type { ChatMessageTemplatesFile } from 'messageTemplates/gql';
import EventTracking from 'lib/EventTracking';
import {
  updateRecipientsCounts,
  clearSelection,
  saveEnvelope,
  Envelope,
} from 'reducers/roleBasedMessagingSearch';

import { ComposerContext, UIEventContext } from './ComposerContext';
import SplitComposer, { MessageType } from './SplitComposer';
import ComposerDataContainer from './ComposerDataContainer';
import { SelectedSocialMediaAccounts } from 'chat/components/ShareOnSocialMedia/ShareOnSocialMediaHoverCard';

type Props = {
  skipSelectRecipients?: boolean;
  messageType: MessageType;
  onRequestHide: () => void;
  onComposerPage?: boolean;
  onSaveMessageTemplate: (composerData: {
    body: string;
    files: ChatMessageTemplatesFile[];
    originalLanguageCode: string;
    targetLanguageCodes: string[];
    predefinedTranslations: ChatPredefinedMessageTranslationsInput[];
    personalizedVoiceMessageFile?: string | null;
  }) => Promise<{ success: boolean }>;
  draftAnnouncement?: Message;
  initialTargets?: Target[];
  onMessageSent?: (options: { scheduledTime?: Date; targets: Target[] }) => void;
  initialSocialMediaAccounts?: SelectedSocialMediaAccounts;
  initialPVMFileUuid?: string;
  isEditingMessageTemplate?: boolean;
  isCreatingMessageTemplate?: boolean;
  isUsingMessageTemplate?: boolean;
};

type RouterProps = {
  router: InjectedRouter;
};

type ReduxDispatchProps = {
  updateRecipientsCounts: () => void;
  clearSelection: () => void;
  saveEnvelope: (x: Envelope) => void;
};

type DefaultProps = {
  messageType: MessageType;
  onRequestHide: () => void;
};

type State = {
  isOpen: boolean;
  // Controls whether to show the "Discard selection?" modal on cancel
  significantChange: boolean;
  // Set when the user clicks "Continue" to move to the composer,
  // so we don't treat it as a cancel action in handleRequestHide
  isContinue: boolean;
};

class SplitComposerModal extends React.Component<Props & ReduxDispatchProps & RouterProps, State> {
  // messageType is optional as a prop but is required in redux, so default it here
  static defaultProps: DefaultProps = { messageType: 'message', onRequestHide: () => {} };

  state: State = {
    isOpen: false,
    significantChange: false,
    isContinue: false,
  };

  handleConfirm = () => {
    this.props.clearSelection();
    this.props.onRequestHide();
  };
  handleCancel = () => this.setState({ isOpen: false });

  generateHandleRequestHide = (uiEventContext: UIEventContext) => () => {
    if (!this.state.isContinue && !this.props.onComposerPage) {
      EventTracking.trackKey(`announcements.share_to.cancel.click`, uiEventContext);

      if (this.state.significantChange) {
        this.setState({ isOpen: true });
        return;
      }
    }
    this.props.onRequestHide();
  };

  handleSignificantChange = () => {
    if (!this.state.significantChange) {
      this.setState({ significantChange: true });
    }
  };

  handlePageChange = () => {
    this.setState({ isContinue: true });
    this.props.saveEnvelope(
      pick(this.props, [
        'draftAnnouncement',
        'initialTargets',
        'messageType',
        'onMessageSent',
        'initialSocialMediaAccounts',
        'initialPVMFileUuid',
        'isCreatingMessageTemplate',
        'isEditingMessageTemplate',
        'isUsingMessageTemplate',
        'onSaveMessageTemplate',
      ])
    );
    this.props.onRequestHide();
    this.props.router.push('/compose');
  };

  componentDidMount() {
    if (this.props.skipSelectRecipients) {
      this.handlePageChange();
    }

    // This means that the user _switched_ to an urgent message (vs. editing recipients from
    // the "To:" bar on the full page composer.) We should clear the selected recipients in
    // this case since not all targets can receive urgent messages.
    if (this.props.messageType === 'urgent' && !this.props.onComposerPage) {
      this.props.clearSelection();
    } else {
      this.props.updateRecipientsCounts();
    }
  }

  render() {
    if (this.props.skipSelectRecipients) {
      return null;
    }

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isOpen} data-test="confirm-close-dialog">
          <ConfirmDialog
            hideCloseButton
            cancelButtonText="Cancel"
            actionButtonText="Discard"
            onCancel={this.handleCancel}
            onConfirm={this.handleConfirm}
            onRequestHide={this.handleCancel}
            title="Discard selection?"
          />
        </Modal>
        <ComposerContext.Consumer>
          {(composerContext) => {
            if (!CurrentUser.isSignedIn()) {
              return null;
            }

            return (
              <Dialog
                size="large"
                className="SplitComposerModal"
                onRequestHide={this.generateHandleRequestHide(composerContext.getUIEventContext())}
                box={false /* a boxed dialog has bigger font size than a box in a dialog */}
              >
                <Box>
                  <BoxBody full>
                    {this.props.onComposerPage ? (
                      <SplitComposer
                        {...this.props}
                        onPageChange={this.props.onRequestHide}
                        onSignificantChangeMade={this.handleSignificantChange}
                      />
                    ) : (
                      <ComposerDataContainer initialTargets={this.props.initialTargets}>
                        {(dataProps) => (
                          <SplitComposer
                            {...this.props}
                            {...dataProps}
                            onPageChange={this.handlePageChange}
                            onSignificantChangeMade={this.handleSignificantChange}
                          />
                        )}
                      </ComposerDataContainer>
                    )}
                  </BoxBody>
                </Box>
              </Dialog>
            );
          }}
        </ComposerContext.Consumer>
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps =>
  bindActionCreators({ updateRecipientsCounts, clearSelection, saveEnvelope }, dispatch);

export { SplitComposerModal };
export default withRouter(
  connect(undefined, mapDispatchToProps)(SplitComposerModal)
  // Hacky way of expressing optional props with default values
) as React.ComponentType<Partial<Props>>;
